import { useState } from 'react'
import { MentionsInput, Mention, SuggestionDataItem, DataFunc, MentionsInputProps } from 'react-mentions'
import { MentionCompanyMember, FetchRequestState } from 'types'
import Styles from './CommentInput.module.scss'
import { CommentInputProps } from './Types'
import client from 'api/client'
import { ProseInput } from 'common/ProseInput/ProseInput'

interface SuggestedCompanyMember extends SuggestionDataItem {
  email?: string
}

const CommentInput = ({
  deprecatedOnEnter,
  auth,
  className,
  onChange,
  isHtmlComment,
  handleKeyDown,
  ...rest
}: CommentInputProps) => {
  const [members, setMembers] = useState<MentionCompanyMember[]>([])
  const [fetching, setFetching] = useState<FetchRequestState>(FetchRequestState.Idle)

  const fetchCompanyMembers = () => {
    if (!auth || [FetchRequestState.Loading, FetchRequestState.Resolved].includes(fetching)) {
      return
    }
    setFetching(FetchRequestState.Loading)
    client.getWorkspaceUsers(auth.company_key).then((res) => {
      if (res.type === 'success') {
        setMembers(
          res.data.map((m) => {
            return { ...m, display: `${m.first_name} ${m.last_name}` }
          }),
        )
        setFetching(FetchRequestState.Resolved)
      } else {
        setFetching(FetchRequestState.Resolved)
      }
    })
  }

  const findCompanyMember: DataFunc = (query: string, cb: (data: MentionCompanyMember[]) => void) => {
    const filteredCompanyMembers = members.filter((user: MentionCompanyMember) =>
      `${user.first_name} ${user.last_name}`.toLocaleLowerCase().includes(query.toLowerCase()),
    )
    if (filteredCompanyMembers.length === 0) {
      cb([])
    } else {
      cb(filteredCompanyMembers)
    }
  }

  const displaySuggestedCompanyMember = (suggestion: SuggestedCompanyMember) => {
    if (suggestion && suggestion.id === 0) return <div className={Styles.emptyCard}>No users found by that name</div>
    return (
      <div className={Styles.card}>
        <div>{suggestion.display}</div>
        <div className={Styles.card_email}>{suggestion.email}</div>
      </div>
    )
  }

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    rest.onFocus?.(e)
    fetchCompanyMembers()
  }

  const handleChange: MentionsInputProps['onChange'] = (_event, value, _plainTextValue, inputMentions) => {
    const mentions = inputMentions
      .map(({ id }) => members.find((m) => m.display_name === id)?.id)
      .filter((m) => m !== null)
    onChange(value, mentions)
  }

  if (isHtmlComment) {
    return (
      <ProseInput
        handleKeyDown={handleKeyDown}
        ref={rest.inputRef}
        className={className}
        placeholder={rest.placeholder}
        initialHtmlString={rest.value ?? ''}
        onChange={(htmlString, _plainText, mentions) =>
          onChange(
            htmlString === '<p></p>' ? '' : htmlString,
            mentions.filter((x) => x.type === 'User').map((x) => x.id),
          )
        }
      />
    )
  }

  return (
    <MentionsInput
      className={`mentions ${Styles.mentionContainer} ${className}`}
      {...rest}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
          deprecatedOnEnter(event.nativeEvent)
        }
      }}
      onChange={handleChange}
      onFocus={handleFocus}
      allowSuggestionsAboveCursor
    >
      <Mention
        trigger="@"
        data={findCompanyMember}
        renderSuggestion={displaySuggestedCompanyMember}
        // note: parseCommentMentionsToHTML() is used to render mentions without brackets
        markup="@{+{+{__display__}+}+}"
        appendSpaceOnAdd={true}
        displayTransform={(_id: any, display: string) => `@${display}`}
        className="mention"
      />
    </MentionsInput>
  )
}

export { CommentInput }
